/*
 * The master inbox's list column (`page.tsx` composes it), in the reply agent's list frame
 * (master-inbox plan, step 3): the heading line with the view's name and its view menu, the label
 * tabs that stand in for the rail's rows while it is collapsed, the incumbent bulk bar that takes the
 * heading's place while rows are ticked, and the list body with its optional Highlights strip, its
 * empty and failed states and its pages. One flat list, newest first by default: no Do first shelf
 * and no fold (Dawid, 2026-10-17, "Flat + Needs reply view").
 */
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { appCount } from "../../src/app-dates.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import {
  INBOX_PAGE_SIZE,
  type InboxConversation,
  type InboxCounts,
  type InboxPage,
} from "../../src/inbox-v2/contract.ts";
import { BulkBar } from "../bulk-bar.tsx";
import {
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  MoreIcon,
  NavGlyph,
  PersonIcon,
  RestoreIcon,
  SeenIcon,
  TrashIcon,
} from "../icons.tsx";
import { type MenuOption, type MenuRow, OptionMenu } from "../option-menu.tsx";
import { Pager } from "../pager.tsx";
import { PendingCount } from "../pending-count.tsx";
import {
  type MailChoice,
  type NavRow,
  type ShellNav,
  sameChoice,
  scopeOptions,
} from "../shell-nav.ts";
import { useFold } from "../use-fold.ts";
import { type Assignment, assignOptions, PeopleNote } from "./assignment.tsx";
import { LabelDot } from "./label-key.tsx";
import { LABEL_VIEWS, RELABEL_CHOICES } from "./labels.ts";
import { ConversationRow } from "./list.tsx";
import { type EmptyContext, ListEmpty, ListFailed } from "./list-empty.tsx";
import type { ListMode, ListSort } from "./list-prefs.ts";
import { RELABEL_OPTIONS } from "./thread.tsx";
import type { NextPlace, Toast } from "./use-inbox.ts";

const conversations = (n: number) => `${appCount(n)} ${n === 1 ? "conversation" : "conversations"}`;

/**
 * A count beside a name, read with its noun: "Inbox, 60 conversations", not "Inbox60". "pending"
 * holds the number's place with a bar while it is on its way (design review "Loading", P3).
 */
function Count(props: { value: number | "pending" | undefined }) {
  if (props.value === undefined) return null;
  if (props.value === "pending") return <PendingCount className="lcr-num" />;
  return (
    <span className="lcr-num">
      <span aria-hidden="true">{appCount(props.value)}</span>
      <span className="lcr-visually-hidden">, {conversations(props.value)}</span>
    </span>
  );
}

/*
 * The tabs that stand in for the rail's mail rows while the rail is icons only, and on a phone: Inbox,
 * home, then the work, Meeting requests and Needs reply with their totals (LEA-494: the two split
 * what needs a reply, so both are in view), then Follow-up due and the label views behind one More
 * menu, as the reply agent's own tabs keep their rarer groups ("Your move, Waiting on lead, More").
 * Like their rail rows, Inbox and the labels carry no total; Follow-up due says its total in the menu
 * (LEA-485, LEA-486).
 *
 * Measured at 390 px (LEA-494): the three named tabs and a worded More need about 393 px, more than
 * a phone's line, so More is its ⋯ mark until one of its views is chosen, and then that view's name.
 * The tabs and More are one strip, so the order menu that does not fit beside them takes the line
 * above (the heading's own rule) and More stays with the tabs, wrapping under them only when a long
 * view name does not fit.
 */
/**
 * The inbox's panel, folded (LEA-501): where the window is too narrow for it, its rows come here.
 * The tabs' More menu gains the boxes, the saved views and the folders, each opened by its address,
 * and the Showing choice stands beside the tabs; the rail's Inbox icon carries the stroke.
 */
export type FoldedPanel = {
  readonly nav: ShellNav;
  readonly onNavigate: (href: string) => void;
};

/**
 * The panel's rows a folded More menu opens by address: the boxes, Drafts among them where it is
 * released (LEA-513), then the views and folders.
 */
const FOLDED_BOXES = new Set(["snoozed", "drafts", "sent", "done", "trash"]);
const HREF = "href:";

function foldedRows(nav: ShellNav): {
  boxes: readonly NavRow[];
  all: readonly NavRow[];
} {
  const boxes = nav.mail.filter((row) => FOLDED_BOXES.has(row.key));
  return { boxes, all: [...boxes, ...nav.views, ...nav.more] };
}

/** A place's glyph in a menu, the panel's own, so a folded row is known by the same mark. */
const glyph = (icon: NavRow["icon"]) => <NavGlyph icon={icon} size={16} />;

const hrefOption = (row: NavRow) => ({
  value: `${HREF}${row.href}`,
  label: row.label,
  mark: glyph(row.icon),
  ...(row.count === null ? {} : { count: { value: row.count, noun: "conversations" } }),
});

/**
 * Groups of menu rows, each after the first opened by a hairline, as the panel parts its own: the
 * folded menus used to run eleven rows on with nothing between (critique, LEA-501).
 */
function parted(...groups: readonly MenuRow<string>[][]): MenuRow<string>[] {
  return groups
    .filter((group) => group.length > 0)
    .flatMap((group, index) =>
      index === 0 || group[0] === undefined
        ? group
        : [{ ...group[0], ruleBefore: true }, ...group.slice(1)],
    );
}

/** The groups a folded panel adds to the More menu, after Follow-up due and the labels. */
function foldedGroups(nav: ShellNav): MenuRow<string>[][] {
  const { boxes } = foldedRows(nav);
  return [
    boxes.map(hrefOption),
    [
      ...(nav.views.length > 0
        ? [
            {
              key: "views",
              label: "Views",
              mark: glyph("view"),
              submenu: nav.views.map(hrefOption),
            },
          ]
        : []),
      ...(nav.more.length > 0
        ? [
            {
              key: "folders",
              label: "More folders",
              mark: glyph("more"),
              submenu: nav.more.map(hrefOption),
            },
          ]
        : []),
    ],
  ];
}

/** The shell's panel folded: on a desktop too narrow for it, on a page of a workspace's inbox. */
export function foldedPanelOf(shell: {
  readonly onPhone: boolean;
  readonly fitted: { readonly panel: number };
  readonly nav: ShellNav | null;
  readonly leave: (href: string) => void;
}): FoldedPanel | null {
  if (shell.onPhone || shell.fitted.panel > 0 || !shell.nav || shell.nav.mail.length === 0)
    return null;
  return { nav: shell.nav, onNavigate: shell.leave };
}

/**
 * Whose threads the list holds, while the panel that holds it is folded: named as a scope ("Showing
 * Everyone"), as the panel names it, and on the order menu's line above the tabs, as quiet as that
 * menu, so the folded heading takes two lines, not three (critique, LEA-501).
 */
function FoldedShowing({ folded }: { folded: FoldedPanel }) {
  const scope = folded.nav.scope;
  if (!scope) return null;
  const choices = [...scope.fixed, ...scope.people];
  return (
    <OptionMenu<string>
      label="Showing"
      icon={null}
      className="lcr-more-groups lcr-list-showing"
      value={scope.value}
      // One span: as loose children of the button's flex row, the space between the words dropped.
      display={
        <span>
          <span className="lcr-rail-owner-word">Showing</span> {scope.label}
        </span>
      }
      options={scopeOptions(scope)}
      onChange={(value) => {
        const href = choices.find((choice) => choice.value === value)?.href;
        if (href) folded.onNavigate(href);
      }}
    />
  );
}

function LabelTabs(props: {
  choice: MailChoice;
  counts: InboxCounts | undefined;
  onChoose: (choice: MailChoice) => void;
  /** The folded panel's rows and Showing; absent on a phone, whose menu sheet holds them. */
  folded?: FoldedPanel | undefined;
}) {
  const { counts, choice } = props;
  const tab = (target: MailChoice, name: string, count: number | undefined) => (
    <button
      type="button"
      className="lcr-tab"
      aria-pressed={sameChoice(choice, target)}
      onClick={() => props.onChoose(target)}
    >
      {name}
      <Count value={count} />
    </button>
  );
  const label = choice.kind === "label" ? choice.label : null;
  const current = LABEL_VIEWS.find((view) => view.view === label);
  const followUp = choice.kind === "follow_up";
  const followUps = counts?.follow_up;
  const placed = props.folded ? foldedRows(props.folded.nav).all.find((row) => row.current) : null;
  const chosen = followUp ? "Follow-up due" : (current?.name ?? placed?.label ?? null);
  return (
    <div className="lcr-list-tab-strip">
      {tab({ kind: "box", box: "inbox" }, "Inbox", undefined)}
      {tab({ kind: "meeting" }, "Meeting requests", counts?.meeting)}
      {tab({ kind: "needs_reply" }, "Needs reply", counts?.needs_reply)}
      <OptionMenu<string>
        label="More views"
        icon={null}
        className="lcr-more-groups"
        pressed={chosen !== null}
        value={followUp ? "follow_up" : (current?.view ?? (placed ? `${HREF}${placed.href}` : ""))}
        display={chosen ?? <MoreIcon size={16} />}
        caret={chosen !== null}
        options={parted(
          [
            {
              value: "follow_up",
              label: "Follow-up due",
              mark: glyph("follow-up"),
              // Its total on the right, where the boxes' sit, not a second line under it.
              ...(followUps === undefined
                ? {}
                : { count: { value: followUps, noun: "conversations" } }),
            },
          ],
          // Each label by its key dot, as the panel and the label pills draw it.
          LABEL_VIEWS.map(({ view, name }) => ({
            value: view,
            label: name,
            mark: glyph({ label: view === "none" ? null : view }),
          })),
          ...(props.folded ? foldedGroups(props.folded.nav) : []),
        )}
        onChange={(value) => {
          if (value.startsWith(HREF)) {
            props.folded?.onNavigate(value.slice(HREF.length));
            return;
          }
          const view = LABEL_VIEWS.find((item) => item.view === value)?.view;
          if (value === "follow_up") props.onChoose({ kind: "follow_up" });
          else if (view) props.onChoose({ kind: "label", label: view });
        }}
      />
    </div>
  );
}

const TAB_ROWS = new Set(["inbox", "meeting", "needs_reply"]);

/**
 * The folded panel on a page with no tabs of its own, a More folder or All mail (LEA-501): the inbox's
 * strip, each row opened by its address, More naming the page. Without it, between the panel's fold
 * and a phone, such a page had no way to the other rows but the rail's Inbox. No Showing: a folder's
 * list is nobody's in particular, so the scope would change nothing on screen.
 */
export function FoldedPlaces({ folded }: { folded: FoldedPanel }) {
  const { nav } = folded;
  const tabs = nav.mail.filter((row) => TAB_ROWS.has(row.key));
  const work = nav.mail.filter((row) => !TAB_ROWS.has(row.key) && !FOLDED_BOXES.has(row.key));
  const placed = [...work, ...foldedRows(nav).all].find((row) => row.current) ?? null;
  return (
    <div className="lcr-list-tabs lcr-list-heading lcr-folded-places">
      <div className="lcr-list-tab-strip">
        {tabs.map((row) => (
          <button
            key={row.key}
            type="button"
            className="lcr-tab"
            aria-pressed={row.current}
            onClick={() => folded.onNavigate(row.href)}
          >
            {row.label}
            <Count value={row.count ?? undefined} />
          </button>
        ))}
        <OptionMenu<string>
          label="More views"
          icon={null}
          className="lcr-more-groups"
          pressed={placed !== null}
          value={placed ? `${HREF}${placed.href}` : ""}
          display={placed?.label ?? <MoreIcon size={16} />}
          caret={placed !== null}
          options={parted(
            work.filter((row) => !row.sub).map(hrefOption),
            work.filter((row) => row.sub).map(hrefOption),
            ...foldedGroups(nav),
          )}
          onChange={(value) => folded.onNavigate(value.slice(HREF.length))}
        />
      </div>
    </div>
  );
}

const SORTS: readonly { value: ListSort; label: string; short: string }[] = [
  { value: "newest", label: "Newest first", short: "Newest" },
  { value: "oldest", label: "Oldest first", short: "Oldest" },
];
const MODES: readonly { value: ListMode; label: string; detail: string }[] = [
  { value: "classic", label: "Classic", detail: "Every reply in one list" },
  {
    value: "highlights",
    label: "Highlights",
    detail: "Up to 3 needing a reply, pinned on top",
  },
];

/** Where an arrow, Home or End moves among a menu's items; undefined for any other key. */
function menuTarget(items: HTMLElement[], key: string): HTMLElement | undefined {
  const active = document.activeElement;
  const index = active instanceof HTMLElement ? items.indexOf(active) : -1;
  if (key === "ArrowDown") return items[(index + 1) % items.length];
  if (key === "ArrowUp") return items[(index - 1 + items.length) % items.length];
  if (key === "Home") return items[0];
  return key === "End" ? items.at(-1) : undefined;
}

const menuItems = (root: HTMLElement | null): HTMLElement[] => [
  ...(root?.querySelectorAll<HTMLElement>("[role=menuitem], [role=menuitemradio]") ?? []),
];

function MenuChoice(props: {
  checked: boolean;
  label: string;
  detail?: string;
  /** Why the choice cannot be made now, said in its place; it stays in the arrows' path. */
  unavailable?: string;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={props.checked}
      aria-disabled={props.unavailable ? true : undefined}
      className="lcr-menu-item"
      onClick={props.unavailable ? undefined : props.onChoose}
    >
      <span className="lcr-menu-item-text">
        <span className="lcr-menu-item-label">{props.label}</span>
        {(props.unavailable ?? props.detail) && (
          <span className="lcr-menu-item-detail">{props.unavailable ?? props.detail}</span>
        )}
      </span>
      {props.checked ? (
        <CheckIcon size={16} />
      ) : (
        <span className="lcr-menu-item-tick-room" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * The heading's view menu: the order, kept per view, and the list's mode, kept per person. It says
 * the order on its face ("Newest"), as the list's heading should; the mode is one choice inside it,
 * not a toggle on the page. Built from the option menu's own parts, with two groups of choices.
 *
 * On a phone it also starts the selection, beside a long press on a row, as the reply agent's
 * filter tray does: the heading carries no Select of its own (master-inbox plan, "Phone behaviour").
 */
export function ViewMenu(props: {
  sort: ListSort;
  /** A reply agent status lists newest first only (step 9): Oldest says so and cannot be chosen. */
  newestOnly?: boolean;
  mode: ListMode;
  onSort: (sort: ListSort) => void;
  onMode: (mode: ListMode) => void;
  /** A phone's way into the selection; absent on a desktop, which ticks rows by hover or X. */
  onSelect?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const items = () => menuItems(root.current);
  useEffect(() => {
    if (!open) return;
    menuItems(root.current)[0]?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };
  const sort = SORTS.find((choice) => choice.value === props.sort) ?? SORTS[0];
  const mode = MODES.find((choice) => choice.value === props.mode) ?? MODES[0];
  const choose = (apply: () => void) => () => {
    apply();
    close();
  };
  return (
    <div className="lcr-option-menu lcr-more-groups lcr-view-menu" ref={root}>
      <button
        ref={button}
        type="button"
        className="lcr-option-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`View: ${sort?.label}, ${mode?.label}`}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" || open) return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span className="lcr-option-value">{sort?.short}</span>
        <ChevronDownIcon size={14} className="lcr-option-caret" />
      </button>
      {open && (
        <div
          className="lcr-popover lcr-option-popover"
          id={menuId}
          role="menu"
          aria-label="View"
          onKeyDown={(event) => {
            if (event.key === "Escape" || event.key === "Tab") {
              if (event.key === "Escape") event.preventDefault();
              return close();
            }
            const target = menuTarget(items(), event.key);
            if (!target) return;
            event.preventDefault();
            target.focus();
          }}
        >
          {props.onSelect && (
            <>
              <button
                type="button"
                role="menuitem"
                className="lcr-menu-item"
                onClick={() => {
                  close();
                  props.onSelect?.();
                }}
              >
                <span className="lcr-menu-item-text">
                  <span className="lcr-menu-item-label">Select conversations</span>
                </span>
              </button>
              <hr className="lcr-view-menu-rule" />
            </>
          )}
          {/* biome-ignore lint/a11y/useSemanticElements: a group of radio items inside role=menu, where a fieldset is not allowed. */}
          <div role="group" aria-label="Sort">
            <p className="lcr-label">Sort</p>
            {SORTS.map((choice) => (
              <MenuChoice
                key={choice.value}
                checked={choice.value === props.sort}
                label={choice.label}
                {...(props.newestOnly && choice.value === "oldest"
                  ? { unavailable: "Reply agent states list newest first" }
                  : {})}
                // Newest-only keeps the view's own stored order for when the agent state is off.
                onChoose={choose(() => (props.newestOnly ? undefined : props.onSort(choice.value)))}
              />
            ))}
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: a group of radio items inside role=menu, where a fieldset is not allowed. */}
          <div role="group" aria-label="List">
            <p className="lcr-label">List</p>
            {MODES.map((choice) => (
              <MenuChoice
                key={choice.value}
                checked={choice.value === props.mode}
                label={choice.label}
                detail={choice.detail}
                onChoose={choose(() => props.onMode(choice.value))}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The heading line: the view's name and total, or, while the rail is icons only, the tabs that name
 * it; then the view menu. It is the line the bulk bar replaces on desktop.
 */
export function ListHeading(props: {
  name: string;
  /** The list's total; "pending" while the list is on its way, undefined where none is known. */
  total: number | "pending" | undefined;
  tabs: boolean;
  /** The inbox's panel, folded on a desktop: its rows join the tabs (LEA-501). */
  folded?: FoldedPanel | undefined;
  choice: MailChoice;
  counts: InboxCounts | undefined;
  menu: ReactNode;
  onChoose: (choice: MailChoice) => void;
}) {
  return (
    <div className="lcr-list-tabs lcr-list-heading">
      {props.tabs ? (
        <LabelTabs
          choice={props.choice}
          counts={props.counts}
          onChoose={props.onChoose}
          folded={props.folded}
        />
      ) : (
        <h2 className="lcr-list-title">
          {props.name}
          <Count value={props.total} />
        </h2>
      )}
      <span className="lcr-list-heading-end">
        {props.folded && <FoldedShowing folded={props.folded} />}
        {props.menu}
      </span>
    </div>
  );
}

/**
 * How many rows are ticked, said politely while the rep selects (the bar draws the count for the
 * eye only). Always in the page, so the first tick is announced, and empty when nothing is ticked.
 */
export function SelectionStatus(props: { selecting: boolean; count: number }) {
  const on = props.selecting && props.count > 0;
  // Whether a selection has just ended, so its end is said too: × and Esc otherwise go unheard.
  const [last, setLast] = useState(on);
  const [ended, setEnded] = useState(false);
  if (last !== on) {
    setLast(on);
    setEnded(!on);
  }
  return (
    <p className="lcr-visually-hidden" role="status">
      {on ? `${appCount(props.count)} selected` : ended ? "Selection cleared" : ""}
    </p>
  );
}

/*
 * How far the selection bar has folded to fit its one row, roomiest first (`useFold`). The box, the
 * count's number, Done, More and × always stay; the rest join More in the order the rep needs them
 * least, so the count is never what gives way (critique of 2026-11-04: at the default 400px list the
 * count had 10px and × sat past the list's edge, its clicks landing on the reading pane).
 */
const BAR_FOLD = {
  /** Keycaps leave the buttons; each keeps its key in its tip. */
  tips: 1,
  /** Mark seen and Mark unseen join More. */
  seen: 2,
  /** Snooze (Unsnooze in Snoozed) joins More. */
  snooze: 3,
  /** Assign joins More, as a group of the same people. */
  assign: 4,
  /** The count says its number alone. */
  count: 5,
  /** Relabel joins More, as a group of the labels. */
  relabel: 6,
  /** Delete (Restore in Trash) joins More. Done is the last action standing. */
  trash: 7,
} as const;

/** What has moved into More at this fold. Assign only exists where assignment is on. */
function movedAt(fold: number, assign: boolean) {
  return {
    seen: fold >= BAR_FOLD.seen,
    snooze: fold >= BAR_FOLD.snooze,
    assign: assign && fold >= BAR_FOLD.assign,
    relabel: fold >= BAR_FOLD.relabel,
    trash: fold >= BAR_FOLD.trash,
  };
}
type Moved = ReturnType<typeof movedAt>;

const ASSIGN = "assign:";
const LABEL = "label:";

/**
 * The label menu's rows for the ticked conversations: the label is current only where every one of
 * them has it, so "No label" is never ticked for rows that carry labels.
 */
const relabelRows = (): MenuOption<string>[] =>
  RELABEL_OPTIONS.map((option) => ({ ...option, value: `${LABEL}${option.value}` }));
const relabelValue = (current: InboxLabelCode | null | undefined) =>
  current === undefined ? "" : `${LABEL}${current ?? "none"}`;

/** More's rows, in the bar's own order: only what the fold has moved there. */
function moreRows(props: SelectionBarProps, moved: Moved): MenuRow<string>[] {
  const { assign } = props;
  const rows: MenuRow<string>[] = [];
  if (moved.relabel)
    rows.push({
      key: "relabel",
      label: "Relabel",
      mark: <LabelDot code={null} />,
      open: props.relabelOpen,
      submenu: relabelRows(),
    });
  if (moved.assign && assign)
    rows.push({
      key: "assign",
      label: "Assign",
      mark: <PersonIcon size={16} />,
      open: assign.open,
      submenu: assignOptions(assign.assignment).map((option) => ({
        ...option,
        value: `${ASSIGN}${option.value}`,
      })),
    });
  if (moved.snooze)
    rows.push(
      props.snoozed
        ? { value: "unsnooze", label: "Unsnooze", mark: <ClockIcon size={16} /> }
        : { value: "snooze", label: "Snooze", mark: <ClockIcon size={16} />, shortcut: "H" },
    );
  if (moved.seen)
    rows.push(
      { value: "seen", label: "Mark seen", mark: <SeenIcon size={16} /> },
      { value: "unseen", label: "Mark unseen", mark: <SeenIcon size={16} />, shortcut: "U" },
    );
  if (moved.trash)
    rows.push({
      value: "trash",
      label: props.inTrash ? "Restore" : "Delete",
      mark: props.inTrash ? <RestoreIcon size={16} /> : <TrashIcon size={16} />,
      shortcut: "#",
      // The one destructive choice stands apart.
      ruleBefore: rows.length > 0,
    });
  return rows;
}

/** What a choice of the bar's menus does: a label is `label:` and its code, an assignee `assign:`. */
function chooseOnBar(props: SelectionBarProps, value: string): void {
  if (value.startsWith(LABEL)) {
    const code = value.slice(LABEL.length);
    props.onRelabel(RELABEL_CHOICES.find((choice) => choice === code) ?? null);
  } else if (value.startsWith(ASSIGN)) props.assign?.onChoose(value.slice(ASSIGN.length));
  else if (value === "snooze") props.onSnooze();
  else if (value === "unsnooze") props.onUnsnooze();
  else if (value === "seen") props.onSeen(true);
  else if (value === "unseen") props.onSeen(false);
  else if (value === "trash") props.onTrash();
}

/**
 * The bar's More (a ⋯ mark), drawn once anything has folded into it. L, M and H open it where their
 * action has moved there: on the labels, the people, or with Snooze's times under the bar.
 */
function BarMore(props: { bar: SelectionBarProps; moved: Moved; empty: boolean }) {
  const { bar, moved } = props;
  const [open, setOpen] = useState(false);
  const relabel = moved.relabel && bar.relabelOpen;
  const assign = moved.assign && (bar.assign?.open ?? false);
  return (
    <>
      <OptionMenu
        label={`More actions for ${conversations(bar.count)}`}
        className="lcr-bulk-more"
        icon={<MoreIcon size={16} />}
        value=""
        display={null}
        caret={false}
        actions
        options={moreRows(bar, moved)}
        onChange={(value) => chooseOnBar(bar, value)}
        showKeys={bar.showKeys}
        disabled={props.empty}
        open={open || relabel || assign}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) return;
          if (relabel) bar.onRelabelOpen(false);
          if (assign) bar.assign?.onOpenChange(false);
        }}
      />
      {moved.snooze && bar.snoozeMenu}
    </>
  );
}

type SelectionBarProps = {
  count: number;
  /** Every row this page shows is ticked, and the list holds more than this page. */
  wholePage: boolean;
  /** "Select all" holds every row of the list (`whole-list.tsx`), on this page or not. */
  wholeList?: boolean;
  header: "none" | "some" | "all";
  /** A desktop with a pointer: keycaps where they fit, and the menus' keys. */
  showKeys: boolean;
  relabelOpen: boolean;
  /** The label every ticked row has; null where they all have none; undefined where they differ. */
  relabelCurrent: InboxLabelCode | null | undefined;
  /** The Snoozed box: the bar wakes them instead (H still opens the times). */
  snoozed: boolean;
  /** Snooze's choices, while open from this bar. */
  snoozeMenu: ReactNode;
  onToggleAll: () => void;
  onDone: () => void;
  /** Trash's own list: the bar restores instead of deleting (LEA-457). */
  inTrash: boolean;
  onTrash: () => void;
  onSeen: (seen: boolean) => void;
  onRelabelOpen: (open: boolean) => void;
  onRelabel: (label: InboxLabelCode | null) => void;
  onSnooze: () => void;
  onUnsnooze: () => void;
  onClear: () => void;
  /** Assign for the ticked rows, where assignment is on; null draws no button. */
  assign: {
    readonly assignment: Assignment;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onChoose: (value: string) => void;
  } | null;
};

/**
 * What the count says: how many, "All 50 on this page" where the page is all ticked and the list
 * holds more (Cmd/Ctrl+A and the box tick a page, never the list), its number alone at the
 * narrowest fold, and what to do while a phone's Select has nothing ticked yet.
 */
function countWords(props: SelectionBarProps, short: boolean): string {
  const number = appCount(props.count);
  if (short) return number;
  if (props.count === 0) return "Tap rows to select";
  if (props.wholeList) return `All ${number} in this list`;
  return props.wholePage ? `All ${number} on this page` : `${number} selected`;
}

/**
 * The bulk bar (`bulk-bar.tsx`) with the inbox's actions: Done, Delete (Restore in Trash), Relabel,
 * Assign, Snooze and the reading pair, folded into More as far as the bar needs (`BAR_FOLD`). Every
 * key works whatever the fold, and each action's tip says how many it touches.
 */
export function SelectionBar(props: SelectionBarProps) {
  const bar = useRef<HTMLDivElement>(null);
  const { assign } = props;
  const content = [String(props.count).length, props.count === 0, props.wholePage, props.wholeList];
  const shape = [...content, assign !== null, props.inTrash, props.snoozed, props.showKeys];
  const fold = useFold(bar, shape.join("|"), BAR_FOLD.trash);
  const moved = movedAt(fold, assign !== null);
  const caps = props.showKeys && fold < BAR_FOLD.tips;
  const key = (name: string) => (caps ? <kbd className="lcr-kbd">{name}</kbd> : null);
  const empty = props.count === 0;
  const many = conversations(props.count);
  return (
    <BulkBar
      bar={bar}
      header={props.header}
      count={countWords(props, fold >= BAR_FOLD.count)}
      onToggleAll={props.onToggleAll}
      onClear={props.onClear}
    >
      <button
        type="button"
        className="lcr-bulk-action"
        aria-keyshortcuts="E"
        title={`Done: ${many} (E)`}
        disabled={empty}
        onClick={props.onDone}
      >
        Done
        {key("E")}
      </button>
      {!moved.trash && (
        <button
          type="button"
          className="lcr-bulk-action"
          aria-keyshortcuts="#"
          title={`${props.inTrash ? "Restore" : "Delete"}: ${many} (#)`}
          disabled={empty}
          onClick={props.onTrash}
        >
          {props.inTrash ? "Restore" : "Delete"}
          {key("#")}
        </button>
      )}
      {!moved.relabel && (
        <OptionMenu
          label={`Relabel ${many}`}
          className="lcr-bulk-menu"
          icon={null}
          value={relabelValue(props.relabelCurrent)}
          display="Relabel"
          caret={false}
          options={relabelRows()}
          onChange={(value) => chooseOnBar(props, value)}
          keycap={key("L")}
          {...(props.showKeys ? { keyshortcuts: "L" } : {})}
          showKeys={props.showKeys}
          disabled={empty}
          open={props.relabelOpen}
          onOpenChange={props.onRelabelOpen}
        />
      )}
      {assign && !moved.assign && (
        <OptionMenu
          label={`Assign ${many}`}
          className="lcr-bulk-menu"
          icon={null}
          value=""
          display="Assign"
          caret={false}
          actions
          options={assignOptions(assign.assignment)}
          onChange={assign.onChoose}
          keycap={key("M")}
          {...(props.showKeys ? { keyshortcuts: "M" } : {})}
          after={<PeopleNote loading={assign.assignment.loading} />}
          disabled={empty}
          open={assign.open}
          onOpenChange={assign.onOpenChange}
        />
      )}
      {!moved.snooze && (
        <span className="lcr-snooze-anchor">
          {props.snoozed ? (
            <button
              type="button"
              className="lcr-bulk-action"
              title={`Unsnooze: ${many}`}
              disabled={empty}
              onClick={props.onUnsnooze}
            >
              Unsnooze
            </button>
          ) : (
            <button
              type="button"
              className="lcr-bulk-action"
              aria-haspopup="dialog"
              aria-expanded={props.snoozeMenu ? true : undefined}
              aria-keyshortcuts="H"
              aria-label="Snooze"
              title={`Snooze: ${many} (H)`}
              disabled={empty}
              onClick={props.onSnooze}
            >
              Snooze
              {key("H")}
            </button>
          )}
          {props.snoozeMenu}
        </span>
      )}
      {!moved.seen && (
        <>
          <button
            type="button"
            className="lcr-bulk-action"
            aria-label="Mark seen"
            aria-keyshortcuts="Shift+I"
            title={`Mark seen: ${many} (Shift+I)`}
            disabled={empty}
            onClick={() => props.onSeen(true)}
          >
            Mark seen
            {key("⇧I")}
          </button>
          <button
            type="button"
            className="lcr-bulk-action"
            aria-label="Mark unseen"
            aria-keyshortcuts="U"
            title={`Mark unseen: ${many} (U)`}
            disabled={empty}
            onClick={() => props.onSeen(false)}
          >
            Mark unseen
            {key("U")}
          </button>
        </>
      )}
      {fold >= BAR_FOLD.seen && <BarMore bar={props} moved={moved} empty={empty} />}
    </BulkBar>
  );
}

export type RowView = {
  showCampaign: boolean;
  focusedId: string | null;
  openId: string | null;
  /** The one row Tab lands on (`ListColumn`): the list is a single Tab stop. */
  tabbableId: string | null;
  selected: ReadonlySet<string>;
  selecting: boolean;
  /** A touch screen while selecting: a tap ticks a row instead of opening it. */
  tapTicks: boolean;
  /** Rows kept from an older list: to open and read, with no box to tick and no hold to select. */
  readOnly: boolean;
  onOpen: (row: InboxConversation) => void;
  /**
   * A row took the focus (Tab onto its box, a click): J, X and Shift+J go on from where the focus is,
   * never from a cursor left on another row.
   */
  onFocus: (id: string) => void;
  /** Tick or untick a row; with Shift, the run from the last row clicked to it. */
  onToggle: (id: string, shiftKey: boolean) => void;
  onLongPress: (id: string) => void;
  /** A row's own Snooze, where a pointer can hover; null on touch. */
  onSnooze: ((row: InboxConversation) => void) | null;
  /** Snooze's choices, where they are open on a row. */
  snoozeMenu: (row: InboxConversation) => ReactNode;
  /** The reply agent's state on a row, for a person with the switch on; null draws nothing. */
  agentState: (id: string) => InboxAgentState | null;
  /** The viewer, so a row assigned to them says "You". */
  you: string | null;
};

function rows(list: readonly InboxConversation[], view: RowView, prefix: string) {
  return list.map((row) => (
    <ConversationRow
      key={`${prefix}${row.id}`}
      row={row}
      showCampaign={view.showCampaign}
      focused={row.id === view.focusedId}
      open={row.id === view.openId}
      selected={view.selected.has(row.id)}
      selecting={view.selecting}
      readOnly={view.readOnly}
      agent={view.agentState(row.id)}
      you={view.you}
      pinned={prefix !== ""}
      tabIndex={prefix === "" && row.id === view.tabbableId ? 0 : -1}
      tapTicks={view.tapTicks}
      onOpen={() => view.onOpen(row)}
      onFocus={() => view.onFocus(row.id)}
      // A range runs in the list's order: a pinned copy in the Highlights strip ticks only itself.
      onToggle={(shiftKey) => view.onToggle(row.id, shiftKey && prefix === "")}
      onLongPress={() => view.onLongPress(row.id)}
      onSnooze={view.onSnooze ? () => view.onSnooze?.(row) : undefined}
      // The menu opens on the list's row, not on its pinned copy.
      snoozeMenu={prefix === "" ? view.snoozeMenu(row) : null}
    />
  ));
}

/**
 * The reply agent filter's refusal to count past its limit (step 9): the server's own words, where
 * the rows would be, and why. It is never a toast, and never an approximate count.
 */
function ListRefused(props: {
  refused: { limit: number; message: string };
  onClearAgent: () => void;
}) {
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">{props.refused.message}</p>
      {/* The server counts the list with every filter but the agent state: this list, not a match. */}
      <p className="lcr-list-empty-detail">
        More than {appCount(props.refused.limit)} conversations in this list.
      </p>
      <div className="lcr-list-empty-links">
        <button type="button" className="lcr-list-empty-link" onClick={props.onClearAgent}>
          Show any agent state
        </button>
      </div>
    </div>
  );
}

/** What the strip's head names: the work row who answers next reads from (LEA-494). */
const STRIP_NAMES = {
  meeting: { name: "Meeting requests", rest: "that asked to meet" },
  needs_reply: { name: "Needs reply", rest: "that need a reply" },
} as const;

/**
 * The Highlights strip: up to three replies from the place who answers next reads from, Meeting
 * requests while any wait, else Needs reply. They stay in the list below too.
 */
function HighlightsHead(props: {
  place: NextPlace;
  total: number;
  shown: number;
  onSeeAll: () => void;
}) {
  const words = STRIP_NAMES[props.place];
  return (
    <div className="lcr-track-head" data-track="important">
      <h3 className="lcr-track-name">
        {words.name}
        <span className="lcr-track-count">
          <span className="lcr-visually-hidden">, </span>
          {appCount(props.total)}
          <span className="lcr-visually-hidden">
            {" "}
            {props.total === 1 ? "conversation" : "conversations"}
          </span>
        </span>
        <span className="lcr-track-hint"> · newest {props.shown}</span>
      </h3>
      {props.total > props.shown && (
        <button type="button" className="lcr-track-more" onClick={props.onSeeAll}>
          Show all
          <span className="lcr-visually-hidden"> {words.rest}</span>
        </button>
      )}
    </div>
  );
}

/** The skeleton's rows: about a screen of them, never a page's worth. */
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5] as const;

/**
 * A first load's rows, before any list is shown (design review "Loading", P1): six rows in the real
 * row's shape, subject and time, lead, preview, two chips. They wait the thread's 180 ms beat before
 * showing, so a quick answer never flashes them, and breathe only where motion is welcome; the
 * heading above them is real from the first frame. A screen reader hears one line.
 */
function ListSkeleton() {
  return (
    <div className="lcr-list-skeleton">
      <p className="lcr-visually-hidden" role="status">
        Loading conversations
      </p>
      <div className="lcr-opening" aria-hidden="true">
        {SKELETON_ROWS.map((row) => (
          <div key={row} className="lcr-skeleton-row">
            <span className="lcr-skeleton-row-line">
              <span className="lcr-skeleton" data-width="row-subject" />
              <span className="lcr-skeleton" data-width="row-time" />
            </span>
            <span className="lcr-skeleton-row-line">
              <span className="lcr-skeleton" data-width="row-lead" />
            </span>
            <span className="lcr-skeleton-row-line">
              <span className="lcr-skeleton" data-width="row-preview" />
            </span>
            <span className="lcr-skeleton-row-chips">
              <span className="lcr-skeleton" data-width="row-chip" />
              <span className="lcr-skeleton" data-width="row-chip-wide" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ListBody(props: {
  main: InboxPage | undefined;
  /** The rows are the list shown before, kept while the next loads: dimmed, and busy. */
  stale: boolean;
  mainFailed: boolean;
  /** The server's reference for the failure, quoted in the notice; null when it gave none. */
  failureReference: string | null;
  retrying: boolean;
  /** The reply agent filter's refusal, in the rows' place; null otherwise. */
  refused: { limit: number; message: string } | null;
  strip: { place: NextPlace; rows: readonly InboxConversation[]; total: number } | null;
  /** What an empty list says, and the ways on it offers. */
  empty: EmptyContext;
  /** Said above the rows while the search index covers only part of the list. */
  coverage: string | null;
  sort: ListSort;
  offset: number;
  rows: RowView;
  onRetry: () => void;
  onPage: (offset: number) => void;
  /** Takes the agent state off and keeps the rest: the refusal's way on. */
  onClearAgent: () => void;
  /** Opens the place the strip reads from. */
  onSeeAll: (place: NextPlace) => void;
}) {
  const { main, strip } = props;
  const list = main?.conversations ?? [];
  return (
    <div
      className="lcr-list-body"
      // Rows kept under a failure are not on their way: dimmed, but not busy.
      aria-busy={(props.stale && !props.mainFailed) || undefined}
      data-stale={props.stale || undefined}
    >
      {props.coverage !== null && list.length > 0 && (
        <p className="lcr-list-coverage" role="note">
          {props.coverage}
        </p>
      )}
      {props.mainFailed && (
        <ListFailed
          reference={props.failureReference}
          retrying={props.retrying}
          onRetry={props.onRetry}
        />
      )}
      {strip && (
        <>
          <HighlightsHead
            place={strip.place}
            total={strip.total}
            shown={strip.rows.length}
            onSeeAll={() => props.onSeeAll(strip.place)}
          />
          {rows(strip.rows, props.rows, "pinned:")}
          <div className="lcr-track-head" data-track="fresh">
            <h3 className="lcr-track-name">
              All replies
              <span className="lcr-track-hint">
                {" "}
                · {props.sort === "newest" ? "newest first" : "oldest first"}
              </span>
            </h3>
          </div>
        </>
      )}
      {rows(list, props.rows, "")}
      {main && list.length === 0 && <ListEmpty context={props.empty} />}
      {props.refused && <ListRefused refused={props.refused} onClearAgent={props.onClearAgent} />}
      {!main && !props.mainFailed && !props.refused && <ListSkeleton />}
      {main && (
        <Pager
          offset={props.offset}
          shown={list.length}
          total={main.total}
          pageSize={INBOX_PAGE_SIZE}
          onPage={props.onPage}
          noun="Conversations"
        />
      )}
    </div>
  );
}

/** The page's reports, newest at the foot: held replies (`held-sends.tsx`), then the last action. */
export function ToastView(props: {
  toast: Toast | null;
  onUndo: () => void;
  /** The toast's other way on, such as Try again (W5). */
  onAction: () => void;
  /** The pointer or the focus is on the reports: their clock stops (`useToast`). */
  onHold: (held: boolean) => void;
  children?: ReactNode;
}) {
  const region = useRef<HTMLDivElement>(null);
  const { onHold } = props;
  useEffect(() => {
    const element = region.current;
    if (!element) return;
    const hold = () => onHold(true);
    const release = (event: Event) => {
      const next = event instanceof FocusEvent ? event.relatedTarget : null;
      if (!(next instanceof Node && element.contains(next))) onHold(false);
    };
    element.addEventListener("pointerenter", hold);
    element.addEventListener("pointerleave", release);
    element.addEventListener("focusin", hold);
    element.addEventListener("focusout", release);
    return () => {
      element.removeEventListener("pointerenter", hold);
      element.removeEventListener("pointerleave", release);
      element.removeEventListener("focusin", hold);
      element.removeEventListener("focusout", release);
    };
  }, [onHold]);
  return (
    <div ref={region} className="lcr-i2-toast-region" role="status" aria-live="polite">
      {props.children}
      {props.toast && (
        <div className="lcr-bulk-toast lcr-i2-toast">
          <span>{props.toast.text}</span>
          {props.toast.undo && (
            <button type="button" className="lcr-bulk-toast-action" onClick={props.onUndo}>
              Undo
            </button>
          )}
          {props.toast.action && (
            <button type="button" className="lcr-bulk-toast-action" onClick={props.onAction}>
              {props.toast.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
